import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { Chart } from "chart.js";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import { NC_CLOSURE_METRICS_DATA } from "../../models/data.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { NonConformiteApiService } from "../../../non-conformite/services/non-conformite/non-conformite-api.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { NcClosureMetricsDialogComponent } from "./nc-closure-metrics-dialog.component";
import {
  periodBoundsFromTimeScale,
  toLocalIsoDay,
} from "../qualite-period-bounds.utils";

@Component({
  selector: "vl-widget-nc-closure-metrics",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    ChipsComponent,
    WidgetPeriodFiltersComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-nc-closure-metrics.component.html",
  styleUrl: "./widget-nc-closure-metrics.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetNcClosureMetricsComponent implements OnDestroy {
  /**
   * `data` input optionnel — utilisé en mode "embedded" (page Analyse NC
   * qui pilote la période globalement). Si non fourni, le widget passe en
   * mode "standalone" et fait son propre fetch avec le filtre temporel.
   */
  data = input<NC_CLOSURE_METRICS_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  canvasRef = viewChild<ElementRef>("timelineChart");
  chart: any;

  private dialogsService = inject(DialogsService);
  private api = inject(NonConformiteApiService);
  private widgetDataFacade = inject(WidgetDataFacadeService);

  /**
   * Échelle temporelle alignée sur `vl-widget-period-filters`.
   * Default = YTD. Ignoré quand `data` input est fourni (mode embedded).
   */
  timeScale = signal<string>("YTD");

  /** Données fetchées en interne (mode standalone). */
  private internalData = signal<NC_CLOSURE_METRICS_DATA | null>(null);
  private internalLoading = signal(false);

  /** Données effectivement utilisées : input prioritaire, sinon internes. */
  displayData = computed(() => this.data() ?? this.internalData());

  /** Loading effectif : metadata input prioritaire, sinon interne. */
  isLoading = computed(() => this.metadata()?.loading ?? this.internalLoading());

  /** Filtres période visibles uniquement en mode standalone. */
  showPeriodFilter = computed(() => !this.data());

  hasData = computed(() => {
    const d = this.displayData();
    return !!d && d.total > 0;
  });

  tauxClotureLabel = computed(() => {
    const v = this.displayData()?.tauxCloture;
    return v == null ? "—" : `${v}%`;
  });

  delaiMoyenLabel = computed(() => {
    const v = this.displayData()?.delaiMoyenJours;
    return v == null ? "—" : `${v} j`;
  });

  slaJ30Label = computed(() => {
    const v = this.displayData()?.slaJ30;
    return v == null ? "—" : `${v}%`;
  });

  constructor() {
    // Mode standalone : refetch quand la période change.
    effect(
      () => {
        const scale = this.timeScale();
        if (this.data()) return;
        this.fetchInternal(scale);
      },
      { allowSignalWrites: true },
    );

    // Refetch sur clic "Actualiser" du dashboard.
    let firstTick = true;
    effect(
      () => {
        const tick = this.widgetDataFacade.refreshTick();
        if (firstTick) {
          firstTick = false;
          return;
        }
        void tick;
        if (this.data()) return;
        this.fetchInternal(this.timeScale());
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const d = this.displayData();
        if (d?.timeline?.length && this.canvasRef()) {
          this.buildChart(d.timeline);
        }
      },
      { allowSignalWrites: true },
    );
  }

  private async fetchInternal(scale: string) {
    this.internalLoading.set(true);
    try {
      const { start, end } = periodBoundsFromTimeScale(scale);
      const startStr = toLocalIsoDay(start);
      const endStr = toLocalIsoDay(end);
      const d = await firstValueFrom(this.api.closureMetrics(startStr, endStr));
      this.internalData.set(d);
    } finally {
      this.internalLoading.set(false);
    }
  }

  private buildChart(timeline: NC_CLOSURE_METRICS_DATA["timeline"]) {
    const canvas = this.canvasRef().nativeElement as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (this.chart) this.chart.destroy();

    // Palette Chart.js par défaut pour cohérence avec les autres widgets.
    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: timeline.map((t) => t.month),
        datasets: [
          {
            label: "Clôturées",
            data: timeline.map((t) => t.cloturees),
            backgroundColor: "#36A2EB",
            stack: "total",
          },
          {
            label: "Ouvertes",
            data: timeline.map((t) => t.ouvertes),
            backgroundColor: "#FF6384",
            stack: "total",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } },
        scales: {
          x: { stacked: true, ticks: { font: { size: 9 } } },
          y: { stacked: true, ticks: { font: { size: 9 } }, beginAtZero: true },
        },
      },
    });
  }

  openFullList() {
    if (!this.hasData()) return;
    this.dialogsService.open(
      NcClosureMetricsDialogComponent,
      "min(95vw, 900px)",
      "fit-content",
      { data: this.displayData() },
    );
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
